import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CupSoda, Droplets, Beer, Zap } from "lucide-react";

/**
 * MasterBrandShowcase — light-background "Our Family of Brands" section that
 * mirrors the Coca-Cola / PepsiCo / Danone master-brand pattern.
 * Left: heading + description. Right: 4 clean white cards, each with the
 * brand ® wordmark, category eyebrow, description and coloured CTA.
 */
const BRANDS = [
  {
    slug: "onecola",
    name: "OneCola",
    category: "Cola Drinks",
    Icon: CupSoda,
    accent: "#DC2626",
    accentSoft: "rgba(220, 38, 38, 0.10)",
    description: "Refreshing cola created for modern consumers seeking great taste and premium quality.",
  },
  {
    slug: "onefresh",
    name: "OneFresh",
    category: "Water & Hydration",
    Icon: Droplets,
    accent: "#10B981",
    accentSoft: "rgba(16, 185, 129, 0.10)",
    description: "Pure hydration inspired by nature with spring water, juices, and healthy refreshment.",
  },
  {
    slug: "maltora",
    name: "Maltora",
    category: "Malt Beverages",
    Icon: Beer,
    accent: "#B45309",
    accentSoft: "rgba(180, 83, 9, 0.10)",
    description: "Premium non-alcoholic malt beverages crafted for rich flavour and everyday enjoyment.",
  },
  {
    slug: "voltx",
    name: "VoltX",
    category: "Energy Drinks",
    Icon: Zap,
    accent: "#2563EB",
    accentSoft: "rgba(37, 99, 235, 0.10)",
    description: "High-performance energy drinks engineered to power work, sport, and active lifestyles.",
  },
];

export default function MasterBrandShowcase() {
  return (
    <section
      id="brands"
      className="relative py-24 lg:py-28 bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-white"
      data-testid="master-brand-showcase"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        {/* Left column — heading */}
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="lg:col-span-2 lg:sticky lg:top-28"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-gold-500 mb-4">Portfolio</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[1.02]">
            Our Family of Brands
          </h2>
          <p className="mt-5 text-neutral-600 dark:text-neutral-300 leading-relaxed max-w-md">
            Every SBC Ltd brand is carefully developed to deliver outstanding quality, trusted ingredients,
            and memorable consumer experiences across international markets.
          </p>
          <Link
            to="/export"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 hover:text-gold-500 transition-colors"
            data-testid="portfolio-view-markets"
          >
            View global markets <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.header>

        {/* Right column — 4 white cards */}
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4 lg:gap-5">
          {BRANDS.map((b, i) => <BrandCard key={b.slug} brand={b} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function BrandCard({ brand, index }) {
  const { slug, name, category, Icon, accent, accentSoft, description } = brand;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.06 * index }}
      className="group relative rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-white/10 p-6 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
      style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.02)" }}
      data-testid={`master-brand-card-${slug}`}
    >
      {/* Brand-tinted accent bar on hover */}
      <span
        className="absolute left-6 top-0 h-1 w-10 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: accent }}
      />

      {/* Icon chip */}
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
           style={{ background: accentSoft, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Brand name with ® superscript */}
      <div className="flex items-baseline gap-1">
        <h3 className="font-display font-black text-2xl tracking-tighter leading-none" style={{ color: accent }}>
          {name}
        </h3>
        <sup className="text-xs font-semibold" style={{ color: accent }}>®</sup>
      </div>

      {/* Category eyebrow */}
      <div className="mt-1 text-[10px] uppercase tracking-[0.28em] font-semibold text-neutral-500 dark:text-neutral-400">
        {category}
      </div>

      {/* Description */}
      <p className="mt-4 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
        {description}
      </p>

      {/* CTA */}
      <Link
        to={`/brands/${slug}`}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold transition-colors"
        style={{ color: accent }}
        data-testid={`master-brand-cta-${slug}`}
      >
        Explore Brand
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </motion.div>
  );
}
